import { useMemo } from "react";
import { 
  AlertTriangle, 
  TrendingUp,
  ExternalLink,
  FileDown
} from "lucide-react";
import { 
  RadarChart, 
  PolarGrid, 
  PolarAngleAxis, 
  Radar, 
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
  Tooltip
} from "recharts";
import { getTierForScore, CALENDAR_LINK, LOGO_URL } from "../../data/quizData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ResultsPage = ({ 
  stageScores, 
  totalScore, 
  maxScore, 
  leadData,
  businessType,
  monthlyRevenue,
  submissionId
}) => {
  const tier = useMemo(() => getTierForScore(totalScore), [totalScore]);
  
  const pdfUrl = submissionId ? `${BACKEND_URL}/api/quiz/pdf/${submissionId}` : null;
  
  const weakestStages = useMemo(() => {
    return [...stageScores]
      .sort((a, b) => (a.score / a.maxScore) - (b.score / b.maxScore))
      .slice(0, 2);
  }, [stageScores]);

  const radarData = useMemo(() => {
    return stageScores.map(s => ({
      stage: s.stageName.split(" ")[0],
      fullName: s.stageName,
      score: Math.round((s.score / s.maxScore) * 100),
      fullMark: 100
    }));
  }, [stageScores]);

  const barData = useMemo(() => {
    return stageScores.map(s => ({
      name: `${s.stageNumber}. ${s.stageName}`,
      score: s.score,
      maxScore: s.maxScore,
      percentage: Math.round((s.score / s.maxScore) * 100),
      isWeak: weakestStages.some(w => w.stageName === s.stageName)
    }));
  }, [stageScores, weakestStages]);

  const getTierClass = () => {
    if (totalScore >= 130) return "tier-optimized";
    if (totalScore >= 100) return "tier-growth-leaks";
    if (totalScore >= 70) return "tier-structural";
    return "tier-unstable";
  };

  const scorePercentage = Math.round((totalScore / maxScore) * 100);

  return (
    <div className="min-h-screen px-4 py-8 md:py-12">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 animate-fadeIn">
          <img src={LOGO_URL} alt="Zaharlab" className="h-12 w-auto mb-4 md:mb-0" />
          <div className="text-slate-400 text-sm">
            Assessment for <span className="text-white font-medium">{leadData?.name}</span>
          </div>
        </div>

        {/* Score Hero */}
        <div className="glass rounded-2xl p-8 md:p-12 mb-8 animate-slideInUp delay-100">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Left: Score */}
            <div>
              <p className="text-slate-400 text-sm uppercase tracking-wider mb-3">Your Total Score</p>
              <div className="flex items-baseline gap-3 mb-4">
                <span className="text-6xl md:text-7xl font-bold gradient-text font-mono text-glow">
                  {totalScore}
                </span>
                <span className="text-3xl text-slate-500">/ {maxScore}</span>
              </div>
              
              <div className="mb-6">
                <div className="score-bar-container h-4">
                  <div 
                    className="score-bar-fill"
                    style={{ width: `${scorePercentage}%` }}
                  />
                </div>
              </div>

              <div className={`tier-badge ${getTierClass()}`}>
                {tier.name}
              </div>
            </div>

            {/* Right: Radar Chart */}
            <div className="h-64 md:h-80">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
                  <PolarGrid stroke="#1E293B" />
                  <PolarAngleAxis 
                    dataKey="stage" 
                    tick={{ fill: '#94A3B8', fontSize: 11 }}
                  />
                  <Radar
                    name="Score"
                    dataKey="score"
                    stroke="#06B6D4"
                    fill="#06B6D4"
                    fillOpacity={0.3}
                    strokeWidth={2}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Assessment Summary */}
        <div className="stage-card mb-8 animate-slideInUp delay-200">
          <h3 className="text-xl font-semibold text-white mb-4">Strategic Assessment</h3>
          <p className="text-slate-300 text-lg leading-relaxed">{tier.description}</p>
        </div>

        {/* Weakest Stages Alert */}
        <div className="glass rounded-xl p-6 mb-8 border-l-4 border-amber-500 animate-slideInUp delay-300">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-1" />
            <div>
              <h4 className="text-lg font-semibold text-white mb-2">Priority Focus Areas</h4>
              <p className="text-slate-400 mb-4">
                Your assessment identified the following stages as requiring immediate attention:
              </p>
              <div className="space-y-2">
                {weakestStages.map((stage, idx) => (
                  <div key={idx} className="flex items-center gap-3 text-slate-300">
                    <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center text-sm font-bold">
                      {idx + 1}
                    </span>
                    <span className="font-medium">{stage.stageName}</span>
                    <span className="text-slate-500 font-mono text-sm">
                      ({stage.score}/{stage.maxScore})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Stage Breakdown */}
        <div className="stage-card mb-8 animate-slideInUp delay-400">
          <h3 className="text-xl font-semibold text-white mb-6">Stage-by-Stage Breakdown</h3>
          
          {/* Bar Chart */}
          <div className="h-80 mb-8">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={barData} 
                layout="vertical"
                margin={{ left: 120, right: 30 }}
              >
                <XAxis type="number" domain={[0, 20]} tick={{ fill: '#64748B' }} />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  tick={{ fill: '#94A3B8', fontSize: 12 }}
                  width={110}
                />
                <Tooltip
                  contentStyle={{
                    background: '#0F172A',
                    border: '1px solid #1E293B',
                    borderRadius: '8px',
                    color: '#F8FAFC'
                  }}
                  formatter={(value, name, props) => [
                    `${value}/${props.payload.maxScore} (${props.payload.percentage}%)`,
                    'Score'
                  ]}
                />
                <Bar dataKey="score" radius={[0, 4, 4, 0]}>
                  {barData.map((entry, index) => (
                    <Cell 
                      key={index}
                      fill={entry.isWeak ? '#EF4444' : '#06B6D4'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Detailed Scores */}
          <div className="space-y-4">
            {stageScores.map((stage, idx) => {
              const isWeak = weakestStages.some(w => w.stageName === stage.stageName);
              const percentage = Math.round((stage.score / stage.maxScore) * 100);
              
              return (
                <div 
                  key={idx}
                  className={`p-4 rounded-lg ${isWeak ? 'weak-stage-highlight' : 'bg-slate-900/30'}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-cyan-400">{stage.stageNumber}.</span>
                      <span className="font-medium text-white">{stage.stageName}</span>
                      {isWeak && (
                        <span className="px-2 py-0.5 rounded text-xs bg-red-500/20 text-red-400 border border-red-500/30">
                          Needs Attention
                        </span>
                      )}
                    </div>
                    <span className="font-mono text-slate-300">
                      {stage.score}/{stage.maxScore}
                    </span>
                  </div>
                  <div className="score-bar-container h-2">
                    <div 
                      className="h-full rounded-full transition-all duration-700"
                      style={{ 
                        width: `${percentage}%`,
                        background: isWeak 
                          ? 'linear-gradient(90deg, #EF4444 0%, #F87171 100%)'
                          : 'linear-gradient(90deg, #06B6D4 0%, #2563EB 100%)'
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA Section */}
        <div className="glass rounded-2xl p-8 md:p-12 text-center mb-8 animate-slideInUp delay-500">
          <TrendingUp className="w-12 h-12 text-cyan-400 mx-auto mb-6" />
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to Optimize Your Growth Engine?
          </h3>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto mb-8">
            Book a Strategic Review Call with Zaharlab to discuss your assessment results and develop a custom action plan for your business.
          </p>
          <a
            href={CALENDAR_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="cta-button inline-flex"
            data-testid="book-call-cta"
          >
            Book Strategic Review Call
            <ExternalLink className="ml-2 w-5 h-5" />
          </a>
        </div>

        {/* PDF Download Section */}
        <div className="stage-card text-center animate-slideInUp delay-500">
          <div className="flex flex-col items-center">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500/20 to-blue-600/20 flex items-center justify-center mb-6 border border-cyan-500/30">
              <FileDown className="w-10 h-10 text-cyan-400" />
            </div>
            <h4 className="text-xl font-semibold text-white mb-3">
              Download Your Assessment Report
            </h4>
            <p className="text-slate-400 max-w-md mb-6">
              Get a professional PDF summary of your 8-Stage Customer Journey Audit results to review offline or share with your team.
            </p>
            {pdfUrl ? (
              <a
                href={pdfUrl}
                download
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-slate-800/80 border border-cyan-500/40 text-cyan-400 font-medium hover:bg-slate-800 hover:border-cyan-500/60 hover:shadow-[0_0_15px_rgba(6,182,212,0.3)] transition-all duration-200"
                data-testid="download-pdf-btn"
              >
                <FileDown className="w-5 h-5" />
                Download PDF Report
              </a>
            ) : (
              <div className="px-6 py-3 rounded-lg bg-slate-800/50 border border-slate-700 text-slate-500">
                PDF generating...
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-12 pt-8 border-t border-slate-800">
          <img src={LOGO_URL} alt="Zaharlab" className="h-10 w-auto mx-auto mb-4 opacity-60" />
          <p className="text-slate-500 text-sm">
            Strategic Growth Partner for Service Businesses
          </p>
        </div>
      </div>
    </div>
  );
};
